import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { getApiErrorMessage } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";

const empty = { name: "" };

export default function Categories() {
    const [categories, setCategories] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/categories");
            setCategories(data || []);
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Failed to load categories"));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return categories;
        return categories.filter((category) => category.name.toLowerCase().includes(term));
    }, [categories, q]);

    const openNew = () => {
        setForm(empty);
        setEditId(null);
        setOpen(true);
    };

    const openEdit = (category) => {
        setForm({ name: category.name || "" });
        setEditId(category.id);
        setOpen(true);
    };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Category name is required");
        setSaving(true);
        try {
            const payload = { name: form.name.trim() };
            if (editId) {
                await api.put(`/categories/${editId}`, payload);
                toast.success("Category updated");
            } else {
                await api.post("/categories", payload);
                toast.success("Category added");
            }
            setOpen(false);
            setEditId(null);
            setForm(empty);
            load();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/categories/${deleteTarget.id}`);
            toast.success("Category deleted");
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error(getApiErrorMessage(err));
        }
    };

    return (
        <div className="space-y-6" data-testid="categories-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Catalogue</div>
                    <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Categories</h1>
                </div>
                <div className="flex gap-2">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            data-testid="categories-search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search categories…"
                            className="pl-9 w-64"
                        />
                    </div>
                    <Button onClick={openNew} data-testid="add-category-btn">
                        <Plus size={16} className="mr-1" /> Add Category
                    </Button>
                </div>
            </div>

            <div className="rounded-md border border-border bg-white overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2} className="text-center py-10 text-muted-foreground">
                                    No categories found.
                                </TableCell>
                            </TableRow>
                        )}
                        {filtered.map((category) => (
                            <TableRow key={category.id} className="data-row">
                                <TableCell className="font-medium">{category.name}</TableCell>
                                <TableCell className="text-right">
                                    <button onClick={() => openEdit(category)} data-testid={`edit-category-${category.id}`} className="p-1 hover:bg-muted rounded">
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => setDeleteTarget(category)} data-testid={`delete-category-${category.id}`} className="p-1 hover:bg-muted rounded text-destructive ml-1">
                                        <Trash2 size={14} />
                                    </button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="category-dialog">
                    <DialogHeader><DialogTitle>{editId ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
                    <div>
                        <Label className="text-xs text-muted-foreground">Category Name *</Label>
                        <Input data-testid="category-name" value={form.name} onChange={(e) => setForm({ name: e.target.value })} autoFocus />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="save-category">
                            {saving ? "Saving…" : editId ? "Update" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Delete Category</DialogTitle></DialogHeader>
                    <div className="text-sm text-muted-foreground">
                        Delete <span className="font-semibold text-foreground">{deleteTarget?.name}</span>? This action cannot be undone.
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={remove}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
